import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  Chip,
  Field,
  Fieldset,
  Input,
  Label,
  Paragraph,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import type { SafetyRepAssignment } from '../../../../api/queries';
import { useGetTeamsWithinOrganizationQuery } from '../../../../hooks';
import { filterTeams, toggleTeam } from '../safetyRepView';
import classes from '../Users.module.css';

type Props = {
  idPrefix: string;
  value: SafetyRepAssignment;
  onChange: (value: SafetyRepAssignment) => void;
  disabled?: boolean;
  error?: string;
};

/** Whether the safety representative is the main one, and which teams they represent */
export const SafetyRepFields = ({ idPrefix, value, onChange, disabled = false, error }: Props): ReactElement => {
  const { t } = useTranslation();
  const { data: teams = [] } = useGetTeamsWithinOrganizationQuery();
  const [query, setQuery] = useState('');
  const shown = filterTeams(teams, query);
  const selected = teams.filter((team) => value.teamIds.includes(team.id));

  return (
    <div className={classes.safetyRep}>
      <Fieldset className={classes.safetyRepFields} aria-invalid={error ? true : undefined}>
        <Fieldset.Legend>{t('organizationPage.users.safetyRep.legend')}</Fieldset.Legend>
        <Fieldset.Description>{t('organizationPage.users.safetyRep.description')}</Fieldset.Description>
        <Checkbox
          id={`${idPrefix}-main`}
          label={t('organizationPage.users.safetyRep.main')}
          description={t('organizationPage.users.safetyRep.mainDescription')}
          checked={value.isMain}
          disabled={disabled}
          onChange={() => onChange({ ...value, isMain: !value.isMain })}
        />
        <Field>
          <Label htmlFor={`${idPrefix}-teams`}>{t('organizationPage.users.safetyRep.teams')}</Label>
          {/* Field links the description and the error to the input, so each is read once */}
          <Field.Description>
            {t(
              value.isMain
                ? 'organizationPage.users.safetyRep.teamsOptional'
                : 'organizationPage.users.safetyRep.teamsRequired',
            )}
          </Field.Description>
          <Input
            id={`${idPrefix}-teams`}
            type="search"
            aria-invalid={error ? true : undefined}
            value={query}
            disabled={disabled}
            onChange={(event) => setQuery(event.target.value)}
          />
          {error && <ValidationMessage>{error}</ValidationMessage>}
        </Field>
        {selected.length > 0 && (
          <div className={classes.chips} aria-label={t('organizationPage.users.safetyRep.selected')}>
            {selected.map((team) => (
              <Chip.Removable
                key={team.id}
                aria-label={t('organizationPage.users.safetyRep.remove', { name: team.name })}
                onClick={() => onChange(toggleTeam(value, team.id))}
              >
                {team.name}
              </Chip.Removable>
            ))}
          </div>
        )}
        <div className={classes.teamList} role="group" aria-label={t('organizationPage.users.safetyRep.teamList')}>
          {shown.map((team) => (
            <Checkbox
              key={team.id}
              data-size="sm"
              label={team.name}
              checked={value.teamIds.includes(team.id)}
              disabled={disabled}
              onChange={() => onChange(toggleTeam(value, team.id))}
            />
          ))}
          {shown.length === 0 && (
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('organizationPage.users.safetyRep.noTeams')}
            </Paragraph>
          )}
        </div>
      </Fieldset>
    </div>
  );
};
